<script setup lang="ts">
import { Download, FileText, MonitorSmartphone, SquareTerminal, X } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import { toast } from "vue-sonner";
import { closeRemoteSession, isOpen, recordingUrl, useRemoteSessions, useRemoteTranscript, type RemoteSession } from "@/api/remote";
import CodeBlock from "@/components/common/CodeBlock.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { confirm } from "@/composables/useConfirm";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { useSession } from "@/composables/useSession";
import { remote as t } from "@/copy/remote";
import { errorMessage } from "@/lib/errors";
import { formatDateTime, formatDuration } from "@/lib/format";
import { formatBytes, statusTone } from "@/lib/remote";

definePage({ meta: { title: "Remote sessions", requires: { anyCapability: ["remote.exec", "remote.read"] } } });

const { me } = useSession();
const canSeeOthers = computed(() => Boolean(me.value?.canApprove));
const everyone = useRouteQuery("all");
const status = useRouteQuery("status");
const sessions = useRemoteSessions(() => ({ all: canSeeOthers.value && everyone.value === "true", status: status.value || undefined }));
const queryClient = useQueryClient();
const closing = ref<string | null>(null);
const transcriptOf = ref<string | null>(null);
const transcript = useRemoteTranscript(transcriptOf);

const statusOptions = Object.entries(t.status).map(([value, label]) => ({ value, label }));
const statusModel = computed({ get: () => status.value || undefined, set: (v: string | string[] | undefined) => (status.value = (v as string) ?? "") });
const isMine = (s: RemoteSession) => !s.owner || s.owner === me.value?.username;

async function close(s: RemoteSession) {
  const answer = await confirm({ title: `${t.close}: ${s.host}?`, description: t.closeConfirm, confirmLabel: t.close, destructive: true });
  if (!answer) return;
  closing.value = s.id;
  try {
    await closeRemoteSession(s.id);
    toast.success(t.closed);
    await queryClient.invalidateQueries({ queryKey: ["remote"] });
  } catch (err) {
    toast.error(errorMessage(err));
  } finally {
    closing.value = null;
  }
}

function toggleTranscript(s: RemoteSession) {
  transcriptOf.value = transcriptOf.value === s.id ? null : s.id;
}
</script>

<template>
  <div>
    <PageHeader :title="t.title" :description="t.description" />

    <FilterBar label="Filter remote sessions" class="mb-5">
      <template v-if="canSeeOthers" #end>
        <div class="flex items-center gap-2">
          <Switch id="all-remote" :model-value="everyone === 'true'" @update:model-value="(v: boolean) => (everyone = v ? 'true' : '')" />
          <Label for="all-remote" class="font-normal">{{ t.everyone }}</Label>
        </div>
      </template>
      <template #chips><FilterChips v-model="statusModel" :options="statusOptions" label="Status" /></template>
    </FilterBar>

    <ListSkeleton v-if="sessions.isPending.value" :rows="4" />
    <ErrorState v-else-if="sessions.error.value" :error="sessions.error.value" what="Remote sessions" @retry="sessions.refetch()" />
    <EmptyState v-else-if="(sessions.data.value ?? []).length === 0" :icon="MonitorSmartphone" :title="t.empty" :description="t.emptyHint" />
    <ul v-else class="flex flex-col gap-2">
      <li v-for="s in sessions.data.value" :key="s.id" class="surface p-4">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
          <div class="flex min-w-0 flex-1 items-start gap-3">
            <span class="grid size-9 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground" aria-hidden="true">
              <component :is="s.kind === 'rdp' ? MonitorSmartphone : SquareTerminal" class="size-4" />
            </span>
            <div class="min-w-0 flex-1 [overflow-wrap:anywhere]">
              <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                <RouterLink :to="`/remote/${s.id}`" class="font-mono text-sm font-medium hover:underline">{{ s.host }}</RouterLink>
                <span class="text-sm text-muted-foreground">{{ t.kinds[s.kind] }}</span>
                <Badge v-if="s.target" variant="outline" class="font-mono">{{ s.target }}</Badge>
                <StatusBadge :status="statusTone(s.status)" :label="t.status[s.status]" />
              </div>
              <dl class="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
                <div class="flex min-w-0 gap-1.5"><dt class="sr-only">Account</dt><dd class="font-mono text-[13px]">{{ s.display }}</dd></div>
                <div v-if="s.owner && !isMine(s)" class="flex gap-1.5"><dt>{{ t.owner }}</dt><dd class="text-foreground">{{ s.owner }}</dd></div>
                <div class="flex gap-1.5" :title="formatDateTime(s.startedAt ?? s.createdAt)">
                  <dt>{{ s.startedAt ? t.started : t.created }}</dt>
                  <dd><RelativeTime :value="s.startedAt ?? s.createdAt" /></dd>
                </div>
                <div v-if="s.durationSeconds !== null" class="flex gap-1.5"><dt>{{ t.duration }}</dt><dd class="tabular-nums">{{ formatDuration(s.durationSeconds * 1000) }}</dd></div>
                <div v-if="s.bytesIn + s.bytesOut > 0" class="flex gap-1.5"><dt>{{ t.traffic }}</dt><dd class="tabular-nums">↑ {{ formatBytes(s.bytesIn) }} · ↓ {{ formatBytes(s.bytesOut) }}</dd></div>
              </dl>
              <p v-if="s.reason" class="mt-1 text-sm"><span class="text-muted-foreground">{{ t.reason }}:</span> {{ s.reason }}</p>
              <p v-if="s.closeReason" class="mt-1 text-sm text-muted-foreground">{{ t.closeReason }}: {{ s.closeReason }}</p>
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-2 border-t pt-3 sm:max-w-[45%] sm:shrink-0 sm:justify-end sm:border-t-0 sm:pt-0">
            <Button v-if="s.status === 'pending' && isMine(s)" size="sm" as-child>
              <RouterLink :to="`/remote/${s.id}`">{{ t.connect }}</RouterLink>
            </Button>
            <Button v-else variant="outline" size="sm" as-child>
              <RouterLink :to="`/remote/${s.id}`">{{ t.open }}</RouterLink>
            </Button>
            <Button v-if="s.kind === 'ssh' && s.recorded" variant="outline" size="sm" :aria-expanded="transcriptOf === s.id" @click="toggleTranscript(s)">
              <FileText /> {{ transcriptOf === s.id ? t.hideTranscript : t.showTranscript }}
            </Button>
            <Button v-if="isOpen(s)" variant="outline" size="sm" :disabled="closing === s.id" @click="close(s)"><X /> {{ t.close }}</Button>
          </div>
        </div>

        <section v-if="transcriptOf === s.id" class="mt-4 flex flex-col gap-2 border-t pt-4" :aria-label="t.transcript">
          <ListSkeleton v-if="transcript.isPending.value" :rows="2" />
          <ErrorState v-else-if="transcript.error.value" :error="transcript.error.value" :what="t.transcript" @retry="transcript.refetch()" />
          <template v-else-if="transcript.data.value">
            <div class="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span v-if="transcript.data.value.truncated">{{ t.transcriptTruncated }}</span>
              <span v-if="transcript.data.value.redactions">{{ t.redactions(transcript.data.value.redactions) }}</span>
              <a :href="recordingUrl(s.id)" class="inline-flex items-center gap-1 hover:text-foreground" download><Download class="size-3.5" /> {{ t.downloadRecording }}</a>
            </div>
            <CodeBlock :code="transcript.data.value.text" :title="t.transcript" wrap max-height="28rem" />
          </template>
        </section>
      </li>
    </ul>
  </div>
</template>
